import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";
import { useWorkspaceSegment } from "./useWorkspaceSegment";
import { adaptPermissionCatalogForSegment } from "../utils/workspaceSegmentLabels";
import { DASHBOARD_FINANCE_ATTENDANCE_READINESS_KEY } from "./useDashboardFinanceAttendanceReadiness";

// Catálogo padrão de permissões (fallback)
export const ACTIONS = ["view", "create", "update", "delete"];
export const CAMPAIGN_ACTIONS = ["view", "create", "update", "delete", "send", "export"];

/** Recursos do menu principal — alinhado a `xpro4api/src/utils/permissionsCatalog.js` */
export const MENU_PERMISSIONS = [
  { key: "dashboard",        label: "Dashboard",                    actions: ["view"] },
  {
    key: "chat",
    label: "Atendimento",
    actions: [
      "view",
      "update",
      "bulk",
      "export",
      "block_others",
      "block_queue",
      "block_bots",
    ],
  },
  { key: "team_chat",        label: "Equipe (chat interno)",        actions: ["view", "manage_channels"] },
  { key: "team_tasks",       label: "Equipe (tarefas)",             actions: ["view", "manage"] },
  { key: "bpe",              label: "Emitir BPe (passagens rodoviárias)", actions: ["view"] },
  { key: "passagens",        label: "Vendas de passagens (controle)", actions: ["view", "export", "request_repasse"] },
  { key: "clients",          label: "Clientes",                     actions: ["view", "manage"] },
  { key: "awareness",        label: "Níveis de Consciência",        actions: ["view"] },
  { key: "sales_pipeline",   label: "Pipeline de Vendas",           actions: ["view"] },
  { key: "surveys",          label: "Pesquisas",                    actions: ACTIONS },
  { key: "campaigns",        label: "Campanhas",                    actions: CAMPAIGN_ACTIONS },
  { key: "virtual_office",   label: "Escritório Virtual",           actions: ["view", "manage"] },
  {
    key: "profile",
    label: "Meu Perfil",
    actions: [
      "view",
      "update_identity",
      "connect_google_calendar",
      "connect_personal_whatsapp",
      "update_preferences",
      "manage_notifications",
      "reset_password",
    ],
  },
];

/** Recursos de Configurações — alinhado à API (fallback / matriz de cargos) */
export const SETTINGS_PERMISSIONS = [
  { key: "products",         label: "Produtos",                     actions: ACTIONS },
  { key: "products_stock",         label: "Estoque de Produtos",                     actions: ["update"] },
  { key: "products_price",         label: "Preço de Produtos",                     actions: ["update"] },
  { key: "cities",           label: "Cidades",                      actions: ACTIONS },
  { key: "users",              label: "Usuários", actions: [...ACTIONS, "change_position"] },
  { key: "chatbot",            label: "Chatbots", actions: ACTIONS },
  { key: "positions",          label: "Cargos", actions: ACTIONS },
  { key: "channels",           label: "Canais", actions: ACTIONS },
  { key: "types",              label: "Tipos", actions: ACTIONS },
  { key: "companies",          label: "Empresas", actions: ACTIONS },
  { key: "automation_rules",   label: "Regras de Automação", actions: ACTIONS },
  { key: "antifraud_methods",  label: "Métodos de Antifraude", actions: ACTIONS },
  { key: "payment_methods",    label: "Formas de Pagamento", actions: ACTIONS },
  { key: "client_tags",        label: "Tags de Cliente", actions: ACTIONS },
  { key: "billing",            label: "Billing e planos", actions: ACTIONS },
  { key: "delivery_methods",   label: "Formas de Entrega", actions: ACTIONS },
  { key: "categories",         label: "Categorias de Produtos", actions: ACTIONS },
  { key: "coupons",            label: "Cupons de Desconto", actions: ACTIONS },
  { key: "awareness_stages",   label: "Etapas dos Níveis de Consciência", actions: ACTIONS },
  { key: "banners",            label: "Banners", actions: ACTIONS },
  { key: "contents",           label: "Conteúdos", actions: ACTIONS },
  { key: "agents",             label: "Agentes", actions: ACTIONS },
  { key: "workspace_i18n",     label: "Idioma do workspace", actions: ["view", "update"] },
  { key: "integrations",       label: "Integrações", actions: ACTIONS },
  { key: "paid_ads",           label: "Anúncios (Meta / Google)", actions: ACTIONS },
  { key: "time_clock",         label: "Controle de Ponto", actions: ["view", "adjust", "export"] },
];

/** Catálogo padrão bruto (sem filtro de segmento) — alinhar à API. */
export const DEFAULT_PERMISSION_CATALOG = [
  { module: "Menu",            resources: MENU_PERMISSIONS },
  { module: "Configurações",   resources: SETTINGS_PERMISSIONS },
];

/** Garante ações novas do fallback mesmo se `/permissions/catalog` estiver defasado. */
export function mergePermissionCatalogWithDefaults(catalog, fallback = DEFAULT_PERMISSION_CATALOG) {
  if (!Array.isArray(catalog) || !catalog.length) return fallback;
  const fallbackByModule = new Map();
  for (const mod of fallback) {
    fallbackByModule.set(mod.module, mod.resources || []);
  }
  const extraByKey = new Map();
  for (const mod of fallback) {
    for (const r of mod.resources || []) {
      extraByKey.set(r.key, Array.isArray(r.actions) ? r.actions : []);
    }
  }
  return catalog.map((mod) => {
    const seen = new Set((mod.resources || []).map((r) => r.key));
    const resources = (mod.resources || []).map((r) => {
      const extra = extraByKey.get(r.key) || [];
      const acts = Array.isArray(r.actions) ? [...r.actions] : [];
      for (const a of extra) {
        if (!acts.includes(a)) acts.push(a);
      }
      return { ...r, actions: acts };
    });
    const fallbackResources = fallbackByModule.get(mod.module) || [];
    for (const r of fallbackResources) {
      if (seen.has(r.key)) continue;
      resources.push({ ...r, actions: Array.isArray(r.actions) ? [...r.actions] : [] });
      seen.add(r.key);
    }
    return { ...mod, resources };
  });
}

export const usePositions = (page = 1, limit = 100, search = "") => {
  const queryClient = useQueryClient();

  const query = useQuery(
    ["positions", page, limit, search],
    () => fetchData(`/positions?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`),
    {
      staleTime: 5000,
      refetchOnWindowFocus: false,
      select: (response) => ({
        positions: response?.data || [],
        pagination: response?.pagination || { total: 0 },
      }),
    }
  );

  const updateMutation = useMutation(
    (position) => {
      const method = position?.id ? "PUT" : "POST";
      const endpoint = position?.id ? `/positions/${position.id}` : `/positions`;
      return fetchData(endpoint, method, position);
    },
    { onSuccess: () => {
      queryClient.invalidateQueries("positions");
      queryClient.invalidateQueries("me-permissions");
      queryClient.invalidateQueries(DASHBOARD_FINANCE_ATTENDANCE_READINESS_KEY);
    } }
  );

  const deleteMutation = useMutation(
    (id) => fetchData(`/positions/${id}`, "DELETE"),
    { onSuccess: () => {
      queryClient.invalidateQueries("positions");
      queryClient.invalidateQueries(DASHBOARD_FINANCE_ATTENDANCE_READINESS_KEY);
    } }
  );

  return { ...query, updateMutation, deleteMutation };
};

export const usePermissionCatalog = () => {
  const { segment } = useWorkspaceSegment();

  const query = useQuery(
    ["permissionCatalog", "change_position"],
    async () => {
      try {
        const res = await fetchData(`/permissions/catalog`, "GET");
        if (Array.isArray(res?.data) && res.data.length) {
          return mergePermissionCatalogWithDefaults(res.data);
        }
        return DEFAULT_PERMISSION_CATALOG;
      } catch {
        return DEFAULT_PERMISSION_CATALOG;
      }
    },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );

  const catalog = useMemo(
    () =>
      adaptPermissionCatalogForSegment(
        mergePermissionCatalogWithDefaults(query.data || DEFAULT_PERMISSION_CATALOG),
        segment
      ),
    [query.data, segment]
  );

  return { catalog, isLoading: query.isLoading, error: query.error, segment };
};
